import React from "react";

import Panel from "../panels/Panel";
import Grid from "../grid/Grid";
import Button from "../buttons/Button";

import "./Selector.scss";
import { Side } from "../../types/system/Layout";
import GridColumn from "../grid/GridColumn";

interface SelectorListProps<T> {
    source: T[];
    value: T[];
    keyField: string;
    className?: string;

    children?:
        | React.ReactElement<GridColumn<T>>
        | React.ReactElement<GridColumn<T>>[];

    onChange?: (value: T[]) => void;
}

interface SelectorListState<T> {
    availableSelectedRecords: T[];
    selectedSelectedRecords: T[];
    value: T[];
}

class SelectorList<T> extends React.Component<
    SelectorListProps<T>,
    SelectorListState<T>
> {
    state = {
        availableSelectedRecords: [],
        selectedSelectedRecords: [],
        value: [],
    } as SelectorListState<T>;

    componentDidMount(): void {
        this.setState(() => ({ value: this.props.value }));
    }

    getAvailable() {
        return this.props.source.filter(
            item =>
                !this.state.value.find(
                    value =>
                        value[this.props.keyField] ===
                        item[this.props.keyField],
                ),
        );
    }

    setValue(value: T[]) {
        this.setState(() => ({
            value: value,
        }));
        this.props.onChange?.(value);
    }
    addOne() {
        this.setValue(
            [...this.state.value].concat(this.state.availableSelectedRecords),
        );
        this.setState(() => ({
            availableSelectedRecords: [],
            selectedSelectedRecords: this.state.availableSelectedRecords,
        }));
    }
    addAll() {
        this.setValue(this.props.source);
        this.setState(() => ({
            availableSelectedRecords: [],
            selectedSelectedRecords: this.state.availableSelectedRecords,
        }));
    }
    remOne() {
        this.setValue(
            this.state.value.filter(
                item =>
                    !this.state.selectedSelectedRecords.find(
                        value =>
                            value[this.props.keyField] ===
                            item[this.props.keyField],
                    ),
            ),
        );
        this.setState(() => ({
            selectedSelectedRecords: [],
            availableSelectedRecords: this.state.selectedSelectedRecords,
        }));
    }
    remAll() {
        this.setValue([]);
        this.setState(() => ({
            selectedSelectedRecords: [],
            availableSelectedRecords: this.state.selectedSelectedRecords,
        }));
    }

    toggleAllAvailable() {
        if (
            this.state.availableSelectedRecords.length ===
            this.getAvailable().length
        ) {
            this.setState(() => ({ availableSelectedRecords: [] }));
        } else {
            this.setState(() => ({
                availableSelectedRecords: this.getAvailable(),
            }));
        }
    }
    toggleAllSelected() {
        if (
            this.state.selectedSelectedRecords.length ===
            this.state.value.length
        ) {
            this.setState(() => ({ selectedSelectedRecords: [] }));
        } else {
            this.setState(() => ({
                selectedSelectedRecords: this.state.value,
            }));
        }
    }

    availableSelectionChanged(_: T, selection: T[]) {
        this.setState(() => ({ availableSelectedRecords: selection }));
    }
    availableEntryDoubleClicked(record: T) {
        this.setValue([...this.state.value].concat([record]));
        this.setState(() => ({
            availableSelectedRecords: [],
        }));
    }

    selectedSelectionChanged(_: T, selection: T[]) {
        this.setState(() => ({ selectedSelectedRecords: selection }));
    }
    selectedEntryDoubleClicked(record: T) {
        this.setValue(
            this.state.value.filter(
                item =>
                    record[this.props.keyField] !== item[this.props.keyField],
            ),
        );
        this.setState(() => ({
            selectedSelectedRecords: [],
        }));
    }

    render(): React.ReactNode {
        const available = this.getAvailable();

        return (
            <div className={`selector-list ${this.props.className ?? ""}`}>
                <Panel
                    bufferSide={Side.None}
                    containContent={true}
                    maxHeight="400px"
                    className="available"
                    bordered={true}>
                    <Button
                        className="header"
                        tooltip="Toggle Select All/Deselect All"
                        text={`Available (${available.length})`}
                        onClick={this.toggleAllAvailable.bind(this)}
                    />
                    <div style={{ height: "calc(100% - 30px)" }}>
                        <Grid<T>
                            window={true}
                            squared={true}
                            draggable={true}
                            droppable={true}
                            globalSelect={true}
                            value={available}
                            keyField={this.props.keyField}
                            selectionChanged={this.availableSelectionChanged.bind(
                                this,
                            )}
                            cellDoubleClicked={this.availableEntryDoubleClicked.bind(
                                this,
                            )}
                            gridDragDropped={this.remOne.bind(this)}
                            selectedRecords={
                                this.state.availableSelectedRecords
                            }
                            defaultSortField={this.props.keyField}>
                            {this.props.children}
                        </Grid>
                    </div>
                </Panel>
                <div className="controls">
                    <button
                        data-tip="Add selected"
                        data-place="right"
                        onClick={this.addOne.bind(this)}>
                        <i>chevron_right</i>
                    </button>
                    <button
                        data-tip="Add all"
                        data-place="right"
                        onClick={this.addAll.bind(this)}>
                        <i>chevron_right</i>
                        <i>chevron_right</i>
                    </button>
                    <button
                        data-tip="Remove all"
                        data-place="right"
                        onClick={this.remAll.bind(this)}>
                        <i>chevron_left</i>
                        <i>chevron_left</i>
                    </button>
                    <button
                        data-tip="Remove selected"
                        data-place="right"
                        onClick={this.remOne.bind(this)}>
                        <i>chevron_left</i>
                    </button>
                </div>
                <Panel
                    bufferSide={Side.None}
                    containContent={true}
                    maxHeight="400px"
                    className="selected"
                    bordered={true}>
                    <Button
                        className="header"
                        tooltip="Toggle Select All/Deselect All"
                        text={`Selected (${this.state.value.length})`}
                        onClick={this.toggleAllSelected.bind(this)}
                    />
                    <div style={{ height: "calc(100% - 30px)" }}>
                        <Grid<T>
                            window={true}
                            squared={true}
                            draggable={true}
                            droppable={true}
                            globalSelect={true}
                            value={this.state.value}
                            keyField={this.props.keyField}
                            selectionChanged={this.selectedSelectionChanged.bind(
                                this,
                            )}
                            cellDoubleClicked={this.selectedEntryDoubleClicked.bind(
                                this,
                            )}
                            gridDragDropped={this.addOne.bind(this)}
                            selectedRecords={this.state.selectedSelectedRecords}
                            defaultSortField={this.props.keyField}>
                            {this.props.children}
                        </Grid>
                    </div>
                </Panel>
            </div>
        );
    }
}

export default SelectorList;
